﻿import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useBookDemo } from './BookDemoContext'
import './Navbar.css'
import SmartHireOrgSignInModal from './SmartHireOrgSignInModal'
import SmartHireCandidateSignInModal from './SmartHireCandidateSignInModal'
import { checkLogin, sessionLogout, ORG_DASHBOARD, CANDIDATE_DASHBOARD } from './SmartHireAuth'

const SMARTHIRE_BASE = 'https://possobuild.ai'

const SIGN_IN_LINKS = [
  { label: 'SmartHire', desc: 'AI Interviewing Platform', icon: '🎯', modal: 'org' },
  { label: 'SmartDevelop', desc: 'Career Readiness AI', icon: '📈', modal: 'candidate' },
]

const hasPicture = (pic: string | undefined) => pic && !pic.startsWith('https://graph.microsoft.com')

const getInitials = (name: string) => {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0][0].toUpperCase()
}

const getDashboardUrl = (userType: string) => {
  if (userType === 'orgUser') return ORG_DASHBOARD
  if (userType === 'aspUser') return CANDIDATE_DASHBOARD
  return SMARTHIRE_BASE
}

const PossoBuildLogo = () => (
  <svg width="140" height="24" viewBox="0 0 200 35" aria-label="PossoBuild">
    <g>
      <path d="m27.99,17.5c-1.19-2.23-3.08-4.03-5.36-5.12-1.09-2.29-2.9-4.17-5.12-5.36-2.23,1.19-4.03,3.08-5.12,5.36-2.29,1.09-4.17,2.9-5.36,5.12,1.19,2.23,3.08,4.03,5.36,5.12,1.09,2.29,2.9,4.17,5.12,5.36,2.23-1.19,4.03-3.08,5.12-5.36h0c2.29-1.09,4.17-2.9,5.36-5.12Z" fill="#23faf7"/>
      <path d="m5.61,23.11c0-2.03.51-3.94,1.4-5.61-.9-1.67-1.4-3.58-1.4-5.61,0-1.83.42-3.57,1.16-5.12C2.76,8.68,0,12.77,0,17.5s2.76,8.82,6.77,10.73c-.74-1.55-1.16-3.29-1.16-5.12Z" fill="#7300e6"/>
      <path d="m28.23,6.77c.74,1.55,1.16,3.29,1.16,5.12,0,2.03-.51,3.94-1.4,5.61.9,1.67,1.4,3.58,1.4,5.61,0,1.83-.42,3.57-1.16,5.12,4-1.91,6.77-6,6.77-10.73s-2.76-8.82-6.77-10.73Z" fill="#7300e6"/>
      <path d="m11.89,5.61c2.03,0,3.94.51,5.61,1.4,1.67-.9,3.58-1.4,5.61-1.4,1.83,0,3.57.42,5.12,1.16C26.32,2.76,22.23,0,17.5,0S8.68,2.76,6.77,6.77c1.55-.74,3.29-1.16,5.12-1.16Z" fill="#7300e6"/>
      <path d="m23.11,29.39c-2.03,0-3.94-.51-5.61-1.4-1.67.9-3.58,1.4-5.61,1.4-1.83,0-3.57-.42-5.12-1.16,1.91,4,6,6.77,10.73,6.77s8.82-2.76,10.73-6.77c-1.55.74-3.29,1.16-5.12,1.16Z" fill="#7300e6"/>
      <path d="m12.38,22.62c-2.29-1.09-4.17-2.9-5.36-5.12-.9,1.67-1.4,3.58-1.4,5.61,0,1.83.42,3.57,1.16,5.12,1.55.74,3.29,1.16,5.12,1.16,2.03,0,3.94-.51,5.61-1.4-2.23-1.19-4.03-3.08-5.12-5.36Z" fill="#199ad2"/>
      <path d="m12.38,12.38c1.09-2.29,2.9-4.17,5.12-5.36-1.67-.9-3.58-1.4-5.61-1.4-1.83,0-3.57.42-5.12,1.16-.74,1.55-1.16,3.29-1.16,5.12,0,2.03.51,3.94,1.4,5.61,1.19-2.23,3.08-4.03,5.36-5.12Z" fill="#199ad2"/>
      <path d="m22.62,12.38c2.29,1.09,4.17,2.9,5.36,5.12.9-1.67,1.4-3.58,1.4-5.61,0-1.83-.42-3.57-1.16-5.12-1.55-.74-3.29-1.16-5.12-1.16-2.03,0-3.94.51-5.61,1.4,2.23,1.19,4.03,3.08,5.12,5.36Z" fill="#199ad2"/>
      <path d="m22.62,22.62c-1.09,2.29-2.9,4.17-5.12,5.36,1.67.9,3.58,1.4,5.61,1.4,1.83,0,3.57-.42,5.12-1.16.74-1.55,1.16-3.29,1.16-5.12,0-2.03-.51-3.94-1.4-5.61-1.19,2.23-3.08,4.03-5.36,5.12Z" fill="#199ad2"/>
    </g>
    <g fill="currentColor">
      <path d="m45.04,5.99h10.26c2.6,0,4.63.68,6.11,2.04,1.48,1.36,2.22,3.21,2.22,5.56s-.74,4.2-2.22,5.56c-1.48,1.36-3.52,2.04-6.11,2.04h-4.91v7.35h-5.35V5.99Zm5.35,10.67h4.56c1.06,0,1.87-.26,2.45-.79.58-.53.87-1.29.87-2.28s-.3-1.75-.89-2.28c-.59-.53-1.4-.79-2.44-.79h-4.56v6.14Z" fill="currentColor"/>
                            <path d="m72.15,28.92c-2.6,0-4.68-.78-6.24-2.34-1.56-1.56-2.34-3.63-2.34-6.21s.78-4.68,2.34-6.24c1.54-1.54,3.62-2.31,6.24-2.31s4.7.77,6.24,2.31c1.54,1.54,2.31,3.62,2.31,6.24s-.77,4.67-2.31,6.21c-1.56,1.56-3.64,2.34-6.24,2.34Zm0-3.96c1.14,0,2.02-.39,2.63-1.17.61-.78.92-1.92.92-3.42s-.31-2.64-.92-3.42c-.61-.78-1.49-1.17-2.63-1.17s-2.05.39-2.66,1.17c-.61.78-.92,1.92-.92,3.42s.31,2.64.92,3.42c.61.78,1.5,1.17,2.66,1.17Z" fill="currentColor"/>
                            <path d="m88.87,29.01c-2.58,0-4.49-.45-5.75-1.35-1.26-.9-1.96-2.34-2.11-4.32h4.81c.08.87.36,1.48.84,1.84.47.36,1.24.54,2.3.54,1.84,0,2.76-.53,2.76-1.58,0-.51-.2-.89-.6-1.16-.4-.26-1.14-.48-2.22-.65l-2.12-.35c-3.67-.57-5.51-2.24-5.51-5,0-1.65.63-2.92,1.9-3.82,1.27-.9,3.08-1.35,5.45-1.35,4.88,0,7.41,1.87,7.6,5.61h-4.59c-.02-1.54-1.02-2.31-3.01-2.31-1.67,0-2.5.52-2.5,1.55,0,.46.19.82.57,1.08.38.25.98.44,1.81.57l2.44.35c1.96.3,3.41.86,4.34,1.68.93.82,1.39,1.95,1.39,3.39,0,1.69-.68,2.99-2.04,3.91-1.36.92-3.28,1.38-5.75,1.38Z" fill="currentColor"/>
                            <path d="m105.09,29.01c-2.58,0-4.49-.45-5.75-1.35-1.26-.9-1.96-2.34-2.11-4.32h4.81c.08.87.36,1.48.84,1.84.47.36,1.24.54,2.3.54,1.84,0,2.76-.53,2.76-1.58,0-.51-.2-.89-.6-1.16-.4-.26-1.14-.48-2.22-.65l-2.12-.35c-3.67-.57-5.51-2.24-5.51-5,0-1.65.63-2.92,1.9-3.82s3.08-1.35,5.45-1.35c4.88,0,7.41,1.87,7.6,5.61h-4.59c-.02-1.54-1.02-2.31-3.01-2.31-1.67,0-2.5.52-2.5,1.55,0,.46.19.82.57,1.08.38.25.98.44,1.81.57l2.44.35c1.96.3,3.41.86,4.34,1.68.93.82,1.39,1.95,1.39,3.39,0,1.69-.68,2.99-2.04,3.91-1.36.92-3.28,1.38-5.75,1.38Z" fill="currentColor"/>
                            <path d="m121.81,28.92c-2.6,0-4.68-.78-6.24-2.34-1.56-1.56-2.34-3.63-2.34-6.21s.78-4.68,2.34-6.24c1.54-1.54,3.62-2.31,6.24-2.31s4.7.77,6.24,2.31c1.54,1.54,2.31,3.62,2.31,6.24s-.77,4.67-2.31,6.21c-1.56,1.56-3.64,2.34-6.24,2.34Zm0-3.96c1.14,0,2.02-.39,2.63-1.17.61-.78.92-1.92.92-3.42s-.31-2.64-.92-3.42c-.61-.78-1.49-1.17-2.63-1.17s-2.05.39-2.66,1.17c-.61.78-.92,1.92-.92,3.42s.31,2.64.92,3.42c.61.78,1.5,1.17,2.66,1.17Z" fill="currentColor"/>
                            <path d="m147.02,16.53c1.22.44,2.19,1.17,2.88,2.17.7,1,1.05,2.10,1.05,3.28,0,2.07-.69,3.68-2.07,4.83-1.38,1.15-3.42,1.73-6.1,1.73h-11.02V5.99h10.7c2.49,0,4.38.53,5.65,1.58,1.28,1.06,1.92,2.45,1.92,4.18,0,2.24-1,3.83-3.01,4.78Zm-4.91-6.27h-5v4.81h5c.82,0,1.46-.22,1.92-.67.45-.44.68-1.05.68-1.81,0-.72-.23-1.29-.68-1.71-.45-.42-1.09-.63-1.92-.63Zm-5,14h5.35c.95,0,1.68-.22,2.2-.67.52-.44.78-1.08.78-1.90,0-1.71-.99-2.56-2.98-2.56h-5.35v5.13Z" fill="currentColor"/>
                            <path d="m162.76,21.32v-9.12h4.94v16.34h-4.81v-2.31c-1.27,1.8-2.98,2.69-5.13,2.69-1.73,0-3.11-.52-4.13-1.57s-1.54-2.53-1.54-4.45v-10.7h4.94v9.72c0,1.86.88,2.79,2.63,2.79.89,0,1.63-.3,2.22-.89s.89-1.43.89-2.5Z" fill="currentColor"/>
                            <path d="m174.7,28.54h-4.94V12.2h4.94v16.34Zm-.1-18.21h-4.72v-4.18h4.72v4.18Z" fill="currentColor"/>
                            <path d="m181.73,28.54h-4.94V5.99h4.94v22.55Z" fill="currentColor"/>
                            <path d="m195.06,14.16V5.99h4.94v22.55h-4.81v-2.12c-1.10,1.67-2.73,2.5-4.91,2.5s-3.93-.77-5.19-2.31c-1.29-1.54-1.93-3.62-1.93-6.24s.64-4.70,1.93-6.24c1.27-1.54,3-2.31,5.19-2.31,2.07,0,3.66.78,4.78,2.34Zm-3.42,10.8c2.34,0,3.52-1.53,3.52-4.59s-1.17-4.59-3.52-4.59c-1.10,0-1.95.41-2.57,1.22-.61.81-.92,1.98-.92,3.50,0,2.98,1.16,4.47,3.48,4.47Z" fill="currentColor"/>
                        </g>
  </svg>
)

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [signOpen, setSignOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null)
  const [activeModal, setActiveModal] = useState<string | null>(null)
  const [user, setUser] = useState<{ name: string; email: string; picture?: string; userType: string } | null>(null)
  const dropRef = useRef<HTMLDivElement>(null)
  const openBookDemo = useBookDemo()

  const openModal = (modalKey: string) => {
    setSignOpen(false)
    setExpandedProduct(null)
    setMobileOpen(false)
    setActiveModal(modalKey)
  }

  // Check if user is already signed in (shared session cookie)
  useEffect(() => {
    checkLogin().then(u => setUser(u))
  }, [])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Close sign dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) {
        setSignOpen(false)
        setExpandedProduct(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const navigate = useNavigate()
  const location = useLocation()

  const scrollTo = (id: string) => {
    setMobileOpen(false)
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } })
      return
    }
    const el = document.getElementById(id)
    if (el) {
      // Push a history entry so Back returns to current scroll position
      window.history.pushState({ scrollY: window.scrollY }, '', `#${id}`)
      const offset = 72
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' })
    }
  }

  // Restore scroll position on Back button
  useEffect(() => {
    const onPopState = (e: PopStateEvent) => {
      if (e.state?.scrollY != null) {
        window.scrollTo({ top: e.state.scrollY, behavior: 'smooth' })
      }
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  // Handle scroll after navigating home from another page
  useEffect(() => {
    if (location.pathname === '/' && location.state?.scrollTo) {
      const id = location.state.scrollTo
      window.history.replaceState({}, '')
      setTimeout(() => {
        const el = document.getElementById(id)
        if (el) {
          const offset = 72
          window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' })
        }
      }, 100)
    }
  }, [location])

  return (
    <nav className={`navbar${scrolled ? ' navbar--scrolled' : ''}`}>
      <div className="container">
        <div className="nav-content">
          {/* Logo */}
          <a href="/" className="nav-logo" aria-label="PossoBuild home">
            <PossoBuildLogo />
          </a>

          {/* Desktop links */}
          <ul className="nav-links">
            <li><button className="nav-link-btn" onClick={() => scrollTo('products')}>Products</button></li>
            <li><button className="nav-link-btn" onClick={() => scrollTo('platform')}>Platform</button></li>
            <li><button className="nav-link-btn" onClick={() => scrollTo('industries')}>Industries</button></li>
            <li><button className="nav-link-btn" onClick={() => scrollTo('about')}>About</button></li>
            <li><Link to="/product-tour" className="nav-link-btn" onClick={() => setMobileOpen(false)}>Product Tour</Link></li>
            <li><Link to="/how-it-works" className="nav-link-btn" onClick={() => setMobileOpen(false)}>How It Works</Link></li>
          </ul>

          {/* Right side actions */}
          <div className="nav-actions">
            <button className="btn-primary nav-demo-btn" onClick={() => openBookDemo('Book a Demo')}>Book a Demo</button>
            {user ? (
              /* ── SIGNED-IN STATE ── */
              <div className="sign-drop" ref={dropRef}>
                <button
                  className={`user-toggle${signOpen ? ' user-toggle--open' : ''}`}
                  onClick={() => setSignOpen(v => !v)}
                  aria-haspopup="true"
                  aria-expanded={signOpen}
                >
                  {hasPicture(user.picture) ? (
                    <img className="user-toggle__avatar" src={user.picture} alt="" />
                  ) : (
                    <span className="user-toggle__initials">{getInitials(user.name || user.email)}</span>
                  )}
                  <span className="user-toggle__name">{user.name || user.email}</span>
                  <svg className="sign-drop__chevron" width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {signOpen && (
                  <div className="sign-drop__menu" role="menu">
                    <div className="sign-drop__header">Go to</div>
                    <a href={getDashboardUrl(user.userType)} className="sign-drop__item" role="menuitem" onClick={() => setSignOpen(false)}>
                      <span className="sign-drop__item-icon">{user.userType === 'orgUser' ? '🎯' : '📈'}</span>
                      <span className="sign-drop__item-body">
                        <span className="sign-drop__item-label">{user.userType === 'orgUser' ? 'SmartHire application' : 'SmartDevelop application'}</span>
                        <span className="sign-drop__item-desc">{user.userType === 'orgUser' ? 'AI Interviewing Platform' : 'Career Readiness AI'}</span>
                      </span>
                      <svg className="sign-drop__item-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </a>
                    <button
                      className="sign-drop__item sign-drop__item--signout"
                      role="menuitem"
                      onClick={async () => {
                        setSignOpen(false)
                        await sessionLogout()
                        setUser(null)
                      }}
                    >
                      <span className="sign-drop__item-icon">🚪</span>
                      <span className="sign-drop__item-body">
                        <span className="sign-drop__item-label">Sign Out</span>
                      </span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* ── NOT SIGNED-IN STATE ── */
              <div className="sign-drop" ref={dropRef}>
                <button
                  className={`sign-drop__trigger${signOpen ? ' sign-drop__trigger--open' : ''}`}
                  onClick={() => setSignOpen(v => !v)}
                  aria-haspopup="true"
                  aria-expanded={signOpen}
                >
                  Sign In
                  <svg className="sign-drop__chevron" width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {signOpen && (
                  <div className="sign-drop__menu" role="menu">
                    <div className="sign-drop__header">Sign in to a product</div>
                    {SIGN_IN_LINKS.map(item => (
                      <button
                        key={item.label}
                        className="sign-drop__item"
                        role="menuitem"
                        onClick={() => openModal(item.modal)}
                      >
                        <span className="sign-drop__item-icon">{item.icon}</span>
                        <span className="sign-drop__item-body">
                          <span className="sign-drop__item-label">{item.label}</span>
                          <span className="sign-drop__item-desc">{item.desc}</span>
                        </span>
                        <svg className="sign-drop__item-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className={`mobile-menu-btn${mobileOpen ? ' mobile-menu-btn--open' : ''}`}
            onClick={() => setMobileOpen(v => !v)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="mobile-menu">
            <button className="nav-link-btn" onClick={() => scrollTo('products')}>Products</button>
            <button className="nav-link-btn" onClick={() => scrollTo('platform')}>Platform</button>
            <button className="nav-link-btn" onClick={() => scrollTo('industries')}>Industries</button>
            <button className="nav-link-btn" onClick={() => scrollTo('about')}>About</button>
            <Link to="/product-tour" className="nav-link-btn" onClick={() => setMobileOpen(false)}>Product Tour</Link>
            <Link to="/how-it-works" className="nav-link-btn" onClick={() => setMobileOpen(false)}>How It Works</Link>
            <div className="mobile-menu__divider" />
            {user ? (
              <>
                <div className="mobile-menu__user-info">
                  {hasPicture(user.picture) ? (
                    <img className="user-toggle__avatar" src={user.picture} alt="" />
                  ) : (
                    <span className="user-toggle__initials">{getInitials(user.name || user.email)}</span>
                  )}
                  <span className="mobile-menu__user-name">{user.name || user.email}</span>
                </div>
                <a href={getDashboardUrl(user.userType)} className="mobile-menu__sign-link">
                  {user.userType === 'orgUser' ? '🎯 SmartHire application' : '📈 SmartDevelop application'}
                </a>
                <button
                  className="mobile-menu__sign-link"
                  onClick={async () => { setMobileOpen(false); await sessionLogout(); setUser(null) }}
                >
                  🚪 Sign Out
                </button>
              </>
            ) : (
              <>
                <p className="mobile-menu__sign-label">Sign in to:</p>
                {SIGN_IN_LINKS.map(item => (
                  <button key={item.label} className="mobile-menu__sign-link" onClick={() => openModal(item.modal)}>
                    {item.icon} {item.label}
                  </button>
                ))}
              </>
            )}
            <button className="btn-primary" style={{marginTop:'1rem',width:'100%'}} onClick={() => openBookDemo('Book a Demo')}>Book a Demo</button>
          </div>
        )}
      </div>
      {activeModal === 'org' && <SmartHireOrgSignInModal onClose={() => setActiveModal(null)} />}
      {activeModal === 'candidate' && <SmartHireCandidateSignInModal onClose={() => setActiveModal(null)} />}
    </nav>
  )
}
